"use client";

import { useMemo, useState } from "react";
import {
  Answers,
  QuizConfig,
  QuizQuestion,
  evaluate,
  flattenQuestions,
} from "@/lib/quiz";
import { trackEvent, identifyLead } from "@/lib/analytics";
import { TRACKING_EVENTS } from "@/lib/posthog";
import { OptionCard } from "./OptionCard";
import { cn } from "@/lib/utils";

type Stage = "home" | "capture" | "quiz" | "thankyou" | "disqualified";

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isPhone = (v: string) => v.replace(/[^\d]/g, "").length >= 8;

export function Quiz({ config }: { config: QuizConfig }) {
  const questions = useMemo(() => {
    // liste à plat + titre de section pour l'affichage
    return config.sections.flatMap((s) =>
      s.questions.map((q) => ({ q, sectionTitle: s.title }))
    );
  }, [config]);
  const total = questions.length;

  const [stage, setStage] = useState<Stage>("home");
  const [contact, setContact] = useState({ prenom: "", nom: "", email: "", phone: "" });
  const [answers, setAnswers] = useState<Answers>({});
  const [qIndex, setQIndex] = useState(0);
  const [tier, setTier] = useState<string>("");

  // ---- HOME ----------------------------------------------------------------
  const start = () => {
    trackEvent(TRACKING_EVENTS.CTA_CLICK, { location: "home" });
    trackEvent(TRACKING_EVENTS.CONTACT_OPENED);
    setStage("capture");
  };

  // ---- CAPTURE -------------------------------------------------------------
  const contactValid =
    contact.prenom.trim().length > 1 &&
    contact.nom.trim().length > 1 &&
    isEmail(contact.email) &&
    isPhone(contact.phone);
  const [touched, setTouched] = useState(false);

  const submitContact = () => {
    setTouched(true);
    if (!contactValid) return;
    identifyLead(contact.email, {
      prenom: contact.prenom,
      nom: contact.nom,
      phone: contact.phone,
    });
    trackEvent(TRACKING_EVENTS.FORM_PARTIAL_SUBMISSION, { email: contact.email });
    trackEvent(TRACKING_EVENTS.QUIZ_STARTED);
    setStage("quiz");
  };

  // ---- QUIZ ----------------------------------------------------------------
  const finish = (finalAnswers: Answers) => {
    const result = evaluate(config, finalAnswers);
    trackEvent(TRACKING_EVENTS.QUIZ_COMPLETED, {
      score: result.score,
      tier: result.tier,
    });
    const props = {
      score: result.score,
      tier: result.tier,
      ...flattenAnswerProps(config, finalAnswers),
    };
    setTier(result.tier);
    if (result.disqualified) {
      trackEvent(TRACKING_EVENTS.LEAD_DISQUALIFIED, props);
      setStage("disqualified");
    } else {
      trackEvent(TRACKING_EVENTS.LEAD_QUALIFIED, props);
      setStage("thankyou");
    }
  };

  const goNextFrom = (nextAnswers: Answers) => {
    if (qIndex < total - 1) setQIndex(qIndex + 1);
    else finish(nextAnswers);
  };

  const answerSingle = (q: QuizQuestion, value: string) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    trackEvent(TRACKING_EVENTS.QUIZ_QUESTION_ANSWERED, {
      question: q.id,
      value,
      index: qIndex + 1,
    });
    goNextFrom(next); // auto-avance (mobile, rapide)
  };

  const toggleMulti = (q: QuizQuestion, value: string) => {
    const cur = Array.isArray(answers[q.id]) ? (answers[q.id] as string[]) : [];
    const next = cur.includes(value)
      ? cur.filter((v) => v !== value)
      : [...cur, value];
    setAnswers({ ...answers, [q.id]: next });
  };

  const confirmMulti = (q: QuizQuestion) => {
    const value = (answers[q.id] as string[]) ?? [];
    trackEvent(TRACKING_EVENTS.QUIZ_QUESTION_ANSWERED, {
      question: q.id,
      value,
      index: qIndex + 1,
    });
    goNextFrom({ ...answers, [q.id]: value });
  };

  const back = () => {
    if (stage === "quiz" && qIndex > 0) setQIndex(qIndex - 1);
    else if (stage === "quiz") setStage("capture");
  };

  // ==========================================================================
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-5">
      {stage === "home" && (
        <Centered>
          <Badge>Bootcamp Manager Augmenté</Badge>
          <h1 className="mt-5 text-balance text-3xl font-extrabold leading-tight">
            {config.meta.title}
          </h1>
          <p className="mt-4 text-pretty text-[15px] text-neutral-300">
            {config.meta.subtitle}
          </p>
          <PrimaryButton onClick={start} className="mt-8">
            {config.meta.ctaStart}
          </PrimaryButton>
          <p className="mt-3 text-xs text-neutral-500">
            ⏱️ ~2 min · {total} questions · 100 % QCM
          </p>
        </Centered>
      )}

      {stage === "capture" && (
        <Centered>
          <Badge>Avant de commencer</Badge>
          <h2 className="mt-5 text-2xl font-bold">Où envoie-t-on vos résultats ?</h2>
          <p className="mt-3 text-sm text-neutral-300">{config.contact.intro}</p>
          <div className="mt-6 space-y-3 text-left">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom" value={contact.prenom}
                onChange={(v) => setContact({ ...contact, prenom: v })} />
              <Field label="Nom" value={contact.nom}
                onChange={(v) => setContact({ ...contact, nom: v })} />
            </div>
            <Field label="Email" type="email" value={contact.email}
              onChange={(v) => setContact({ ...contact, email: v })}
              invalid={touched && !isEmail(contact.email)} />
            <Field label="Téléphone" type="tel" value={contact.phone}
              onChange={(v) => setContact({ ...contact, phone: v })}
              invalid={touched && !isPhone(contact.phone)} />
          </div>
          {touched && !contactValid && (
            <p className="mt-3 text-xs text-red-400">
              Renseignez un email et un téléphone valides pour recevoir vos résultats.
            </p>
          )}
          <PrimaryButton onClick={submitContact} className="mt-6">
            Commencer le test →
          </PrimaryButton>
          <p className="mt-3 text-[11px] leading-relaxed text-neutral-500">
            {config.contact.consent}
          </p>
        </Centered>
      )}

      {stage === "quiz" && (() => {
        const { q, sectionTitle } = questions[qIndex];
        const multiSelected = Array.isArray(answers[q.id]) ? (answers[q.id] as string[]) : [];
        return (
          <div className="flex min-h-dvh flex-col">
            {/* progress sticky */}
            <div className="sticky top-0 z-10 bg-neutral-950/90 pb-3 pt-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <button onClick={back} className="text-neutral-400 hover:text-white" aria-label="Retour">←</button>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                    style={{ width: `${((qIndex + 1) / total) * 100}%` }} />
                </div>
                <span className="shrink-0 text-xs tabular-nums text-neutral-400">
                  {qIndex + 1}/{total}
                </span>
              </div>
              <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-indigo-300">
                {sectionTitle}
              </p>
            </div>

            <div className="flex flex-1 flex-col pb-6 pt-2">
              <h2 className="text-xl font-bold leading-snug">{q.question}</h2>
              {q.help && <p className="mt-1 text-sm text-neutral-400">{q.help}</p>}
              {q.type === "multi" && (
                <p className="mt-1 text-xs text-neutral-500">Plusieurs choix possibles</p>
              )}

              <div className="mt-5 space-y-2.5">
                {q.options.map((opt) => {
                  const selected =
                    q.type === "multi"
                      ? multiSelected.includes(opt.value)
                      : answers[q.id] === opt.value;
                  return (
                    <OptionCard
                      key={opt.value}
                      label={opt.label}
                      multi={q.type === "multi"}
                      selected={selected}
                      onClick={() =>
                        q.type === "multi"
                          ? toggleMulti(q, opt.value)
                          : answerSingle(q, opt.value)
                      }
                    />
                  );
                })}
              </div>

              {q.type === "multi" && (
                <PrimaryButton
                  onClick={() => confirmMulti(q)}
                  disabled={multiSelected.length === 0}
                  className="mt-6"
                >
                  Continuer →
                </PrimaryButton>
              )}
            </div>
          </div>
        );
      })()}

      {stage === "thankyou" && (
        <Centered>
          <span className="inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-300">
            Merci {contact.prenom} 🎉
          </span>
          <h2 className="mt-5 text-2xl font-extrabold">C'est noté, vos réponses sont enregistrées.</h2>
          <p className="mt-4 text-[15px] text-neutral-300">{config.meta.resultsDelivery}</p>
          <p className="mt-3 text-sm text-neutral-400">
            Nos experts analysent votre profil et reviennent vers vous très vite par message.
          </p>
        </Centered>
      )}

      {stage === "disqualified" && (
        <Centered>
          <h2 className="text-2xl font-bold">Merci de votre participation 🙏</h2>
          <p className="mt-4 text-[15px] text-neutral-300">
            Ce programme s'adresse aux managers et chefs de projet en activité (salariés
            ou freelances). Votre situation actuelle ne correspond pas aux critères, mais
            on garde vos réponses et on revient vers vous si une offre plus adaptée arrive.
          </p>
        </Centered>
      )}
    </main>
  );
}

// ---- petits composants UI --------------------------------------------------
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center py-12 text-center">
      <div className="w-full">{children}</div>
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium tracking-wide text-neutral-300">
      {children}
    </span>
  );
}

function PrimaryButton({
  children, onClick, className, disabled,
}: {
  children: React.ReactNode; onClick: () => void; className?: string; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full rounded-2xl bg-indigo-500 px-6 py-4 text-base font-semibold text-white transition active:scale-[0.99] hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-40",
        className
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label, value, onChange, type = "text", invalid = false,
}: {
  label: string; value: string; onChange: (v: string) => void; type?: string; invalid?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-neutral-400">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={type === "email" ? "email" : type === "tel" ? "tel" : "on"}
        inputMode={type === "tel" ? "tel" : type === "email" ? "email" : "text"}
        className={cn(
          "w-full rounded-xl border bg-white/5 px-3.5 py-3 text-[15px] text-white outline-none transition placeholder:text-neutral-500 focus:border-indigo-400",
          invalid ? "border-red-500/70" : "border-white/15"
        )}
      />
    </label>
  );
}

// Sérialise les réponses en propriétés plates pour PostHog (q_<id> = valeur).
function flattenAnswerProps(config: QuizConfig, answers: Answers): Record<string, any> {
  const out: Record<string, any> = {};
  for (const q of flattenQuestions(config)) {
    if (answers[q.id] !== undefined) out[`q_${q.id}`] = answers[q.id];
  }
  return out;
}
